/**
 * Constants and global state for reactivity system
 */

import { debugConstants } from './debug';

// Create a global singleton to ensure all modules share the same state
const GLOBAL_KEY = '__JELENJS_REACTIVE_STATE__';

interface GlobalReactiveState {
  currentEffect: EffectFunction | null;
  currentContext: ReactiveContext | null;
  microtaskPending: boolean;
  globalEffectId: number;
}

// Get or create global state
function getGlobalState(): GlobalReactiveState {
  if (typeof globalThis !== 'undefined') {
    if (!(globalThis as any)[GLOBAL_KEY]) {
      (globalThis as any)[GLOBAL_KEY] = {
        currentEffect: null,
        currentContext: null,
        microtaskPending: false,
        globalEffectId: 0
      };
    }
    return (globalThis as any)[GLOBAL_KEY];
  }
  
  // Fallback for environments without globalThis
  if (typeof window !== 'undefined') {
    if (!(window as any)[GLOBAL_KEY]) {
      (window as any)[GLOBAL_KEY] = {
        currentEffect: null,
        currentContext: null,
        microtaskPending: false,
        globalEffectId: 0
      };
    }
    return (window as any)[GLOBAL_KEY];
  }
  
  // Final fallback - create local state (not ideal but better than nothing)
  return {
    currentEffect: null,
    currentContext: null,
    microtaskPending: false,
    globalEffectId: 0
  };
}

const globalState = getGlobalState();

// Export getters and setters that use the global state
export let currentEffect: EffectFunction | null = globalState.currentEffect;
export let currentContext: ReactiveContext | null = globalState.currentContext;
export let microtaskPending: boolean = globalState.microtaskPending;
export let globalEffectId: number = globalState.globalEffectId;

// Effect states using bitmasks (inspired by SolidJS)
export const CLEAN = 0;   // Effect is clean (no updates needed)
export const STALE = 1;   // Effect is stale (needs to be run)
export const PENDING = 2; // Effect is pending (in queue or running)

// Execution counter for batching (inspired by SolidJS)
export let execCount = 0;

// Default equality function
export const defaultEquals = Object.is;

// Types for reactivity system
export interface EffectFunction {
  (): void;
  id: number;
  disposed: boolean;
  running: boolean;
  state: number;
  cleanupFns: (() => void)[];
  sources: SignalNode[];
  sourceSlots: number[];  // SolidJS-inspired: track positions for O(1) removal
  parentContext: ReactiveContext | null;
  dispose: () => void;
  updatedAt: number | null;  // SolidJS-inspired: track when last updated
}

export interface SignalNode {
  value: any;
  subscribers: Set<EffectFunction>;
  // SolidJS-inspired optimizations
  observers: EffectFunction[] | null;
  observerSlots: number[] | null;
  id?: string;  // Optional ID for debugging
}

export interface ReactiveContext {
  effects: Set<EffectFunction>;
}

// Updater functions
export function setCurrentEffect(value: EffectFunction | null): EffectFunction | null {
  debugConstants(`Current effect changing from #${globalState.currentEffect?.id ?? 'null'} to #${value?.id ?? 'null'}`);
  globalState.currentEffect = value;
  currentEffect = value; // Keep local variable in sync
  return globalState.currentEffect;
}

export function setCurrentContext(value: ReactiveContext | null): ReactiveContext | null {
  debugConstants('Current context changing');
  globalState.currentContext = value;
  currentContext = value; // Keep local variable in sync
  return globalState.currentContext;
}

export function incrementEffectId(): number {
  globalState.globalEffectId++;
  globalEffectId = globalState.globalEffectId; // Keep local variable in sync
  debugConstants(`Created new effect #${globalState.globalEffectId}`);
  return globalState.globalEffectId;
}

export function setMicrotaskPending(value: boolean): boolean {
  if (globalState.microtaskPending !== value) {
    debugConstants(`Microtask pending: ${value}`);
  }
  globalState.microtaskPending = value;
  microtaskPending = value; // Keep local variable in sync
  return globalState.microtaskPending;
}

export function incrementExecCount(): number {
  globalState.globalEffectId++; // Reuse existing counter
  execCount = globalState.globalEffectId;
  debugConstants(`Incremented execCount to: ${execCount}`);
  return execCount;
}

export function getExecCount(): number {
  return execCount;
} 